import type { LanguageCode } from './url-language-utils'
import {
  isValidLanguageCode,
  VALID_LANGUAGES,
  withLanguagePath,
} from './url-language-utils'

export function isLobbyEnabled(
  env: Record<string, unknown> = import.meta.env as Record<string, unknown>,
): boolean {
  return String(env.VITE_LOBBY_ENABLED ?? '').toLowerCase() !== 'false'
}

export const LOBBY_PATH = '/lobby'

export const LOBBY_RULES_PATH = '/lobby/rules'

export function postAuthLandingPath(
  onArena: boolean,
  lobbyEnabled: boolean,
): string {
  return onArena && lobbyEnabled ? LOBBY_PATH : '/dashboard'
}

export function lobbyCanonicalUrl(args: {
  origin: string
  interfaceLanguage?: string
  page?: number
}): string {
  const path = args.interfaceLanguage && isValidLanguageCode(args.interfaceLanguage)
    ? withLanguagePath(LOBBY_PATH, args.interfaceLanguage)
    : LOBBY_PATH
  const url = new URL(path, args.origin)
  if (args.page && args.page > 1)
    url.searchParams.set('page', String(args.page))
  return url.toString()
}

export interface LobbyCursor {
  createdAt: Date
  roomCode: string
}

export interface LobbyView {
  languages: LanguageCode[]
  statusFilter: LobbyStatusFilter
  cursors: LobbyCursor[]
}

export function lobbyViewFromSearch(search: string): LobbyView {
  const params = new URLSearchParams(search)
  const languages = [...new Set(
    params.getAll('gameLanguages')
      .flatMap(value => value.split(','))
      .filter((value): value is LanguageCode => isValidLanguageCode(value)),
  )]
  const rawStatus = params.get('statusFilter')
  const statusFilter: LobbyStatusFilter
    = rawStatus === 'all' || rawStatus === 'playing'
      ? rawStatus
      : 'waiting'
  const cursors: LobbyCursor[] = []
  for (const raw of params.getAll('cursor')) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (!Array.isArray(parsed) || parsed.length !== 2)
        break
      const [timestamp, roomCode] = parsed
      if (typeof timestamp !== 'string'
        || typeof roomCode !== 'string'
        || roomCode.length === 0
        || roomCode.length > 64) {
        break
      }
      const createdAt = new Date(timestamp)
      if (Number.isNaN(createdAt.getTime()))
        break
      cursors.push({ createdAt, roomCode })
    }
    catch {
      break
    }
  }
  return { languages, statusFilter, cursors }
}

export function lobbyViewSearch(search: string, view: LobbyView): string {
  const params = new URLSearchParams(search)
  for (const key of ['gameLanguages', 'statusFilter', 'cursor', 'page'])
    params.delete(key)
  if (view.languages.length > 0)
    params.set('gameLanguages', view.languages.join(','))
  if (view.statusFilter !== 'waiting')
    params.set('statusFilter', view.statusFilter)
  for (const cursor of view.cursors) {
    params.append(
      'cursor',
      JSON.stringify([cursor.createdAt.toISOString(), cursor.roomCode]),
    )
  }
  if (view.cursors.length > 0)
    params.set('page', String(view.cursors.length + 1))
  const value = params.toString()
  return value ? `?${value}` : ''
}

export function languageNameKey(code: string): string {
  const valid = isValidLanguageCode(code) ? code : 'en'
  return `lobby.languages.${valid}`
}

export const LOBBY_FILTER_LANGUAGES: readonly LanguageCode[] = VALID_LANGUAGES

export function toggleLobbyLanguage(
  current: readonly LanguageCode[],
  code: LanguageCode,
): LanguageCode[] {
  const next = current.includes(code)
    ? current.filter(value => value !== code)
    : [...current, code]
  return next.length === 0 ? [] : next
}

export type LobbyJoinOutcome
  = | 'joined'
    | 'full'
    | 'started'
    | 'gone'
    | 'unavailable'
    | 'registration'
    | 'credits'
    | 'noFreeGames'
    | 'failed'

export function lobbyJoinOutcome(message: string | undefined): LobbyJoinOutcome {
  switch (message) {
    case 'room_full': {
      return 'full'
    }
    case 'room_not_open': {
      return 'started'
    }
    case 'room_not_found': {
      return 'gone'
    }
    case 'room_unavailable_to_you':
    case 'game_start_forbidden_contact_support': {
      return 'unavailable'
    }
    case 'registration_required':
    case 'UNAUTHORIZED': {
      return 'registration'
    }
    case 'join_requires_credits': {
      return 'credits'
    }
    case 'no_free_games_left': {
      return 'noFreeGames'
    }
    default: {
      return 'failed'
    }
  }
}

export function lobbyJoinOutcomeKey(outcome: LobbyJoinOutcome): string {
  return `lobby.join.${outcome}`
}

export type LobbyEventAction
  = | { kind: 'prepend' }
    | { kind: 'replace' }
    | { kind: 'remove' }
    | { kind: 'notify' }
    | { kind: 'resync' }
    | { kind: 'ignore' }

export interface LobbyEventContext {
  pageIndex: number
  languages: readonly string[]
  visibleRoomCodes: readonly string[]
}

export function lobbyEventAction(
  event:
    | { type: 'room.listed', room: { roomCode: string, language: string | null } }
    | { type: 'room.updated', room: { roomCode: string, language: string | null } }
    | { type: 'room.delisted', roomCode: string }
    | { type: 'stream.resync' },
  context: LobbyEventContext,
): LobbyEventAction {
  if (event.type === 'stream.resync')
    return { kind: 'resync' }

  if (event.type === 'room.delisted') {
    return context.visibleRoomCodes.includes(event.roomCode)
      ? { kind: 'remove' }
      : { kind: 'ignore' }
  }

  const matchesFilter
    = context.languages.length === 0
      || (event.room.language !== null
        && context.languages.includes(event.room.language))

  if (event.type === 'room.updated') {
    return context.visibleRoomCodes.includes(event.room.roomCode)
      ? { kind: 'replace' }
      : { kind: 'ignore' }
  }

  if (!matchesFilter)
    return { kind: 'ignore' }

  if (context.visibleRoomCodes.includes(event.room.roomCode))
    return { kind: 'ignore' }

  return context.pageIndex === 0 ? { kind: 'prepend' } : { kind: 'notify' }
}

export const LOBBY_RULE_KEYS = [
  'takeover',
  'async',
  'notify',
  'anyone',
  'splitCost',
  'freeQuota',
  'leaving',
  'language',
] as const

export type LobbyRuleKey = (typeof LOBBY_RULE_KEYS)[number]

export type LobbyStatusFilter = 'all' | 'waiting' | 'playing'

export const STATUS_CHIPS: ReadonlyArray<{
  value: LobbyStatusFilter
  labelKey: string
}> = [
  { value: 'waiting', labelKey: 'lobby.statusWaiting' },
  { value: 'playing', labelKey: 'lobby.statusPlaying' },
  { value: 'all', labelKey: 'lobby.statusAll' },
]

export function resolveLobbyCta(row: {
  cta: string
  inProgress?: boolean
  settled: boolean
  isMember: boolean
}): string | null {
  if (row.settled)
    return 'unavailable'
  if (row.isMember)
    return 'return'
  if (row.inProgress)
    return null
  return row.cta
}
